import type { UseQueryResult } from "@tanstack/react-query/src/types.ts";
import type { ResponseConfig, ResponseErrorConfig } from "client.ts";
import type { GetDataWrapperResult } from "components/MutateWrapper.tsx";
import type { Dayjs } from "dayjs";
import {
  type AssetPosition,
  type GetAssetTimeseriesDataAssetAssetIdTimeSeriesGet422,
  type GetAssetTimeseriesDataAssetAssetIdTimeSeriesGetQueryResponse,
  type ParsedTimeSeriesResponse,
  type RawTimeSeriesResponse,
  type TimeSeriesDataProjectionEnum,
  useGetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet,
  useGetAssetTimeseriesDataAssetAssetIdTimeSeriesGet,
} from "kubb";
import { isAsset, isAssetPosition } from "model/assetPosition.tsx";
import {
  type DeviceTimeSeriesData,
  convertTimesToDayjs,
  extendDataToTimeRange,
} from "model/deviceTimeSeries.tsx";
import type { Asset } from "utils/object.tsx";
import useGetDataNew from "./getData.tsx";

export function useGetAssetTimeSeriesData(
  device: Asset | AssetPosition,
  start_datetime?: Dayjs,
  end_datetime?: Dayjs,
  projection?: TimeSeriesDataProjectionEnum,
): GetDataWrapperResult<DeviceTimeSeriesData> {
  function load(): UseQueryResult<
    | ResponseConfig<GetAssetTimeseriesDataAssetAssetIdTimeSeriesGetQueryResponse>
    | undefined,
    ResponseErrorConfig<GetAssetTimeseriesDataAssetAssetIdTimeSeriesGet422>
  > {
    if (isAsset(device)) {
      return useGetAssetTimeseriesDataAssetAssetIdTimeSeriesGet(
        device.asset_id,
        {
          start_datetime: start_datetime?.format(),
          end_datetime: end_datetime?.format(),
          projection,
          company_id: device.company_id,
        },
      );
    }

    if (isAssetPosition(device)) {
      return useGetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet(
        device.asset_position_id,
        {
          start_datetime: start_datetime?.format(),
          end_datetime: end_datetime?.format(),
          company_id: device.company_id,
        },
      );
    }
    throw new Error("Device is neither asset nor asset position.");
  }

  const response = useGetDataNew<
    RawTimeSeriesResponse | ParsedTimeSeriesResponse
  >(load);

  return {
    ...response,
    data:
      response.data &&
      extendDataToTimeRange(
        response.data.columns,
        response.data.data,
        response.data.errors,
        "device_errors" in response.data
          ? response.data.device_errors
          : undefined,
        convertTimesToDayjs(response.data?.times),
        start_datetime,
        end_datetime,
      ),
  };
}
